import noop from 'lodash/noop';
import React, { useState } from 'react';
import styled from 'styled-components';
import ErrorMessage from '@zocdoc-frontend-common/components-errormessage';
import { AddInsuranceCardButton } from '@zocdoc-frontend-common/components-insuranceform';
import {
    EVENT_INITIATORS,
    INTERACTION_TYPES,
    Metrics,
    MetricsClickable,
    MetricsScrollable,
} from '@zocdoc-frontend-common/utils-frontend-metrics';
import { useEffectOnce } from '@zocdoc-frontend-common/utils-react';
import { PrimaryCheckbox } from '@zocdoc-mezzanine/components-checkbox';
import { slate30, white } from '@zocdoc-mezzanine/foundations-colors';
import {
    labelSmall,
    paragraphSmall,
} from '@zocdoc-mezzanine/foundations-typography';
import InsuranceCardLightBox from 'src/Booking/components/InsuranceCardLightBox/InsuranceCardLightBoxView';

/**
 * @param imgSrcFront {image} front of card image
 * @param imgSrcBack {image} back of card image
 * @param isInsuranceCardShared {boolean} true if patient is sharing insurance card with provider
 * @param onClickContinue {function} handles what to do with insurance cards once scanned in scanner
 * @param onShareInsuranceCheckboxChange {function} handles toggle of sharing insurance card checkbox
 * @param sendMetric {function} function that sends metrics
 * @param isScannerOpen {boolean} is scanner in open state
 * @param isLoading {boolean} is scanner in loading state
 * @param setScannedCardScannerOpenState {function} function that will set isScannerOpen
 * @param setScannedCardLoadingState {function} function that will set isScannerLoading
 * @param hasFetchError {boolean} true if issues fetching correct insurance images that were just scanned
 * @returns {JSX.Element}
 */
export const InsuranceCardScannerSectionView = ({
    imgSrcFront = null,
    imgSrcBack = null,
    isInsuranceCardShared,
    onClickContinue = noop,
    onShareInsuranceCheckboxChange = noop,
    sendMetric = noop,
    isLoading,
    setScannedCardLoadingState = noop,
    isScannerOpen,
    setScannedCardScannerOpenState = noop,
    hasFetchError = false,
}) => {
    const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false);
    const closeImagePreview = () => {
        setIsImagePreviewOpen(false);
        sendMetric({
            action: 'Close insurance image preview',
            element: 'InsuranceCardImageSection',
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.CLICK,
        });
    };
    const openImagePreview = () => {
        setIsImagePreviewOpen(true);
    };
    if (imgSrcFront === null) {
        return (
            <>
                <AddInsuranceCardButton
                    onClickContinue={onClickContinue}
                    isLoading={isLoading}
                    setIsLoading={setScannedCardLoadingState}
                    isScanCardOpen={isScannerOpen}
                    setIsScanCardOpen={setScannedCardScannerOpenState}
                />
                <FetchError
                    hasFetchError={hasFetchError}
                    sendMetric={sendMetric}
                />
            </>
        );
    }
    const images = imgSrcBack ? [imgSrcFront, imgSrcBack] : [imgSrcFront];
    return (
        <Metrics component="Booking" section="InsuranceCardScannerSection">
            <MetricsClickable
                element="ShareInsuranceWithProviderCheckbox"
                action={`Insurance Card is ${
                    !isInsuranceCardShared ? 'shared' : 'not shared'
                } with provider`}
            >
                <PrimaryCheckbox
                    id="share-insurance-card-checkbox"
                    checked={isInsuranceCardShared}
                    data-test="share-insurance-card-checkbox"
                    onChange={onShareInsuranceCheckboxChange}
                >
                    <CheckboxLabel data-test="share-insurance-card-checkbox-label">
                        Share insurance card with provider and save for future
                        bookings
                    </CheckboxLabel>
                </PrimaryCheckbox>
            </MetricsClickable>

            <InsuranceCardSectionWrapper>
                <InsuranceCardLabel data-test="insurance-card-label">
                    Insurance card
                </InsuranceCardLabel>
                <UpdateInsuranceCardContainer>
                    <AddInsuranceCardButton
                        isUpdateCard={true}
                        onClickContinue={onClickContinue}
                        isLoading={isLoading}
                        setIsLoading={setScannedCardLoadingState}
                        isScanCardOpen={isScannerOpen}
                        setIsScanCardOpen={setScannedCardScannerOpenState}
                    />
                </UpdateInsuranceCardContainer>

                <MetricsClickable
                    element="InsuranceCardImageSection"
                    action="Click insurance image preview"
                >
                    <MetricsScrollable
                        element="InsuranceCardImageSection"
                        action="View insurance image preview"
                    >
                        {({ ref }) => (
                            <StyledImage
                                ref={ref}
                                alt="Insurance card thumbnail"
                                className="exclude-from-playback"
                                data-test="insurance-card-front-thumbnail"
                                src={imgSrcFront}
                                onClick={openImagePreview}
                            />
                        )}
                    </MetricsScrollable>
                </MetricsClickable>
                <InsuranceCardLightBox
                    images={images}
                    showInsuranceCardLightBox={isImagePreviewOpen}
                    closeImagePreview={closeImagePreview}
                />
            </InsuranceCardSectionWrapper>
            <FetchError
                hasFetchError={hasFetchError}
                sendMetric={sendMetric}
                isUpdateError={imgSrcFront !== null}
            />
        </Metrics>
    );
};

const FetchError = ({ hasFetchError, sendMetric, isUpdateError }) => {
    if (hasFetchError) {
        useEffectOnce(() => {
            sendMetric({
                element: 'InsuranceCardImageSection',
                action: `View ${
                    isUpdateError ? 'update' : 'add'
                } insurance image fetch error`,
            });
        });
        return (
            <ErrorContainer data-test="scanned-image-fetch-error">
                <ErrorMessage>
                    {`We’re unable to upload your ${isUpdateError ? 'new' : ''}
                    insurance card at this time.`}
                </ErrorMessage>
            </ErrorContainer>
        );
    }
    return null;
};

const InsuranceCardSectionWrapper = styled.div`
    position: relative;
    height: 79px;
    left: 0%;
    right: 0.58%;
    background: ${white};
    border: 1px solid ${slate30};
    box-sizing: border-box;
    flex: none;
    order: 1;
    flex-grow: 0;
    margin: 48px 0px 24px 0px;
`;

const CheckboxLabel = styled.div`
    position: absolute;
    height: 48px;
    left: 28px;
    right: 0px;
    top: -1px;
    ${paragraphSmall};
    line-height: 24px;
`;

const StyledImage = styled.img`
    position: absolute;
    width: 80px;
    height: 50px;
    margin-bottom: 13px;
    border-radius: 4px;
    margin-top: 13px;
    right: 13px;
`;

const InsuranceCardLabel = styled.div`
    position: absolute;
    height: 24px;
    margin-left: 14px;
    margin-top: 13px;
    ${labelSmall}
`;
const UpdateInsuranceCardContainer = styled.div`
    position: absolute;
    height: 24px;
    margin-left: 14px;
    margin-top: 38px;
    margin-right: 10px;
    white-space: normal;
`;

const ErrorContainer = styled.div`
    margin: -20px 0px 20px 0px;
`;
